import { Injectable } from '@angular/core';
import { ILoginService } from './login.service.interface';
import { HttpRequestService } from 'src/app/shared/services/http-request/http-request.service';
import { ApiRoutingUrl } from 'src/app/shared/Constants/api-routing-url';
import { Observable, of } from 'rxjs';
import { AuthenticationResult } from '../../models/authentication-result';
import { map, catchError, switchMap } from 'rxjs/operators';
import { User } from 'src/app/shared/models/user';
import { AuthenticationError } from '../../models/authentication-error';
import { GDPRAgreement } from 'src/app/shared/models/gdpr-agreement';
import { HttpStatusCode } from 'src/app/shared/Constants/http-status-code';
import { Messages } from 'src/app/shared/Constants/messages';

@Injectable({
  providedIn: 'root'
})
export class LoginService implements ILoginService {
  constructor(private _http: HttpRequestService) { }

  public authenticateWithCredentials(username: string, password: string): Observable<AuthenticationResult> {
    let payload = this.getAuthenticationPayload(username, password);
    return this.authenticate(payload);
  }

  public authenticateWithIdentityToken(identityToken: string, encodedUsername: string): Observable<AuthenticationResult> {
    let payload = this.getIdentityAuthenticationPayload(identityToken, encodedUsername);
    return this.authenticateUsingSDPIdentity(payload);
  }

  public authenticateWithExternalToken(externalToken: string, encodedUsername: string, expiresIn: number): Observable<AuthenticationResult> {
    let payload = this.getExternalAuthenticationPayload(externalToken, encodedUsername, expiresIn);
    return this.authenticate(payload);
  }

  public requestPassword(username: string): Observable<{ success: boolean, message: string, title: string }> {
    return this._http.post(ApiRoutingUrl.forgotCredentials, JSON.stringify(username), { headers: { 'Content-Type': 'application/json' } }).pipe(
      map(result => {
        return {
          success: true,
          message: result,
          title: Messages.FORGOT_CREDENTIALS_SUCCESS_title
        };
      }),
      catchError(error => {
        let result = {
          success: false,
          message: error.error || Messages.FORGOT_CREDENTIALS_UNKNOWN_ERROR,
          title: error && error.status ? (error.status == HttpStatusCode.NotFound ? Messages.FORGOT_CREDENTIALS_NOT_FOUND_ERROR_title : Messages.FORGOT_CREDENTIALS_SEND_EMAIL_ERROR_title) : Messages.FORGOT_CREDENTIALS_SEND_EMAIL_ERROR_title
        };
        return of(result);
      })
    );
  }

  private getTokenExpiryDateTime(response: any): Date {
    let tokenExpiryDateTime = new Date();
    tokenExpiryDateTime.setSeconds(tokenExpiryDateTime.getSeconds() + response.expires_in);
    return tokenExpiryDateTime;
  }

  private fillAuthenticationResult(data: any): AuthenticationResult {
    let user = new User();
    let result = new AuthenticationResult();
    result.success = true;
    result.user = Object.assign(user, data);
    result.user.tokenExpiryDateTime = this.getTokenExpiryDateTime(data);
    result.user.hasConsentedAgreement = true;
    return result;
  }

  private authenticate(payload: string): Observable<any> {
    return this._http.post(ApiRoutingUrl.authUrl, payload,
      { headers: { "Content-Type": "application/x-www-form-urlencoded", "timeout": "15000" } }).pipe(
        switchMap((response) => {
          return of({ authenticationResult: this.fillAuthenticationResult(response), authHttpResonpse: response });
        }),
        switchMap((data) => {
          return this.resolveGDPRAgreementDisplay(data);
        }),
        catchError(error => {
          let result = new AuthenticationResult();
          result.success = false;
          result.error = this.determineAuthenticationError(error);
          return of(result);
        })
      );
  }

  private authenticateUsingSDPIdentity(payload: string): Observable<any> {    
    return this._http.post(ApiRoutingUrl.AuthenticationUsingSDPIdentityUrl, payload,
      { headers: { "Content-Type": "application/x-www-form-urlencoded", "timeout": "15000" } }).pipe(
        switchMap((response) => {
          return of({ authenticationResult: this.fillAuthenticationResult(response), authHttpResonpse: response });
        }),
        switchMap((data) => {
          return this.resolveGDPRAgreementDisplay(data);
        }),
        catchError(error => {
          let result = new AuthenticationResult();
          result.success = false;
          result.error = this.determineAuthenticationError(error);
          return of(result);
        })
      );
  }

  private resolveGDPRAgreementDisplay(data: { authenticationResult: AuthenticationResult, authHttpResonpse: any }) {
    if (data.authHttpResonpse["GDPRAgreementId"]) {
      data.authenticationResult.user.hasConsentedAgreement = false;
      let agreementId = data.authHttpResonpse["GDPRAgreementId"];
      let agreementDate = data.authHttpResonpse["GDPRAgreementDate"];
      return this.getGDPRAgreement(agreementId, agreementDate).pipe(
        switchMap((content) => {
          data.authenticationResult.gdprAgreementContent = content;
          return of(data.authenticationResult);
        }));
    }
    return of(data.authenticationResult);
  }

  private getGDPRAgreement(agreementId: any, agreementDate: any): Observable<GDPRAgreement> {
    return this._http.get(`${ApiRoutingUrl.userAgreementContent}/${agreementId}`).pipe(
      map(content => {
        return new GDPRAgreement(agreementId, content, agreementDate);
      })
    );
  }

  private getAuthenticationPayload(username: string, password: string): string {
    username = encodeURIComponent(username);
    password = encodeURIComponent(password);
    let postData: any = `grant_type=password&username=${username}&password=${password}&login_type=web`;
    return postData;
  }

  private getExternalAuthenticationPayload(externalToken: string, username: string, expiresIn: number): string {
    username = encodeURIComponent(username);
    let postData: any = `grant_type=password&username=${username}&token=${externalToken}&expiresIn=${expiresIn}&login_type=externalweb`;
    return postData;
  }

  private getIdentityAuthenticationPayload(identityToken: string, username: string): string {
    username = encodeURIComponent(username);
    let postData: any = `grant_type=password&username=${username}&identitytoken=${identityToken}&login_type=web`;
    return postData;
  }

  private determineAuthenticationError(error: any): AuthenticationError {
    let retVal: AuthenticationError = new AuthenticationError();
    if (this.isUnauthorizedError(error)) {
      retVal.ErrorTitle = Messages.AUTH_ERROR_title;
      retVal.ErrorDescription = error && error.error && error.error.error ? error.error.error : Messages.AUTH_ERROR
    } else if (this.isResetPasswordRequiredError(error)) {
      retVal = JSON.parse(error.error.error);
      retVal.ErrorTitle = Messages.AUTH_FIRST_LOGIN_title;
      retVal.RedirectActionDescription = Messages.AUTH_CHANGE_PASSWORD;
    } else {
      retVal.ErrorDescription = Messages.AUTH_ERROR;
      retVal.ErrorTitle = Messages.AUTH_ERROR_title;
    }

    return retVal;
  }

  private isUnauthorizedError(error: any): boolean {
    return error && error.status === HttpStatusCode.Unauthorized;
  }

  private isResetPasswordRequiredError(error: any): boolean {
    return error && error.status === HttpStatusCode.Conflict;
  }
}
